import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { extractApiData, isApiSuccess } from '../../utils/helpers';
import { validateEmail } from '../../utils/validation';
import type {
  ForgotPasswordInitiateResponse,
  ForgotPasswordVerifyOtpResponse,
} from './models/types';
import './Login.css';
import {
  BsEyeFill,
  BsEyeSlashFill,
  BsClockHistory,
  BsCheckCircleFill,
} from 'react-icons/bs';
import Loader from '../shared/Loader';
import { AuthBrand, AuthShell } from './AuthLayout';

type ForgotStep = 'email' | 'otp' | 'password' | 'success';

function extractInitiateData(body: unknown): ForgotPasswordInitiateResponse {
  const data = extractApiData<ForgotPasswordInitiateResponse>(body);
  if (data) return data;
  return (body ?? {}) as ForgotPasswordInitiateResponse;
}

function extractVerifyData(body: unknown): ForgotPasswordVerifyOtpResponse {
  const data = extractApiData<ForgotPasswordVerifyOtpResponse>(body);
  if (data) return data;
  return (body ?? {}) as ForgotPasswordVerifyOtpResponse;
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<ForgotStep>('email');
  const [email, setEmail] = useState('');
  const [resetRequestId, setResetRequestId] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    const emailError = validateEmail(email, { required: true });
    if (emailError) {
      setError(emailError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.initiateForgotPassword(email.trim());
      if (!isApiSuccess(response)) {
        setError((response as { message?: string })?.message || 'Unable to send reset OTP.');
        return;
      }

      const data = extractInitiateData(response);
      const requestId = data.resetRequestId;
      const message =
        data.message ||
        (response as { message?: string })?.message ||
        'If the email is registered, a password reset OTP has been sent.';

      if (requestId) {
        setResetRequestId(requestId);
        setOtp('');
        setStep('otp');
        setInfo(message);
      } else {
        setInfo(message);
      }
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Unable to send reset OTP. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.verifyForgotPasswordOtp({
        email: email.trim(),
        resetRequestId,
        otp,
      });

      if (!isApiSuccess(response)) {
        setError((response as { message?: string })?.message || 'OTP verification failed.');
        return;
      }

      const data = extractVerifyData(response);
      if (!data.resetToken) {
        setError('Reset token missing in response.');
        return;
      }

      setResetToken(data.resetToken);
      setPassword('');
      setConfirmPassword('');
      setStep('password');
      setInfo(data.message || 'OTP verified. Set your new password below.');
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Invalid OTP. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Password and confirm password do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.resetPassword({
        resetToken,
        password,
        confirmPassword,
      });

      if (!isApiSuccess(response)) {
        setError((response as { message?: string })?.message || 'Unable to reset password.');
        return;
      }

      setStep('success');
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Unable to reset password. The reset token may have expired.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const stepTitle =
    step === 'email'
      ? 'Forgot password'
      : step === 'otp'
        ? 'Verify OTP'
        : step === 'password'
          ? 'Set new password'
          : 'Password updated';

  const stepSub =
    step === 'email'
      ? 'Enter your email and we will send a one-time code'
      : step === 'otp'
        ? 'Enter the 6-digit code sent to your email'
        : step === 'password'
          ? 'Choose a new password for your account'
          : 'You can now sign in with your new password';

  return (
    <AuthShell>
      <AuthBrand />

      <div className="ln-form-header">
        <h1 className="ln-form-title">{stepTitle}</h1>
        <p className="ln-form-sub">{stepSub}</p>
      </div>

      {error && (
        <div className="ln-error">
          <BsClockHistory />
          <span>{error}</span>
        </div>
      )}

      {info && !error && step !== 'success' && (
        <div className="ln-error ln-error--info">
          <BsCheckCircleFill />
          <span>{info}</span>
        </div>
      )}

      {step === 'email' && (
        <form onSubmit={handleEmailSubmit} className="ln-form">
          <label className="ln-label">
            <span>Email</span>
            <div className="ln-field">
              <input
                className="ln-input"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
          </label>

          <button type="submit" className="ln-submit" disabled={isLoading}>
            {isLoading ? <><Loader size="sm" tone="light" style={{ marginRight: 8 }} /> Sending OTP…</> : 'Send reset code'}
          </button>
        </form>
      )}

      {step === 'otp' && (
        <form onSubmit={handleOtpSubmit} className="ln-form">
          <label className="ln-label">
            <span>Email</span>
            <div className="ln-field">
              <input className="ln-input" type="email" value={email} disabled />
            </div>
          </label>

          <label className="ln-label">
            <span>Enter 6-digit OTP</span>
            <div className="ln-field">
              <input
                className="ln-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                placeholder="123456"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                autoComplete="one-time-code"
              />
            </div>
          </label>

          <button type="submit" className="ln-submit" disabled={isLoading}>
            {isLoading ? <><Loader size="sm" tone="light" style={{ marginRight: 8 }} /> Verifying…</> : 'Verify OTP'}
          </button>

          <button
            type="button"
            className="ln-submit ln-submit-ghost"
            onClick={() => {
              setStep('email');
              setOtp('');
              setResetRequestId('');
              setError('');
              setInfo('');
            }}
            disabled={isLoading}
          >
            Use a different email
          </button>
        </form>
      )}

      {step === 'password' && (
        <form onSubmit={handlePasswordSubmit} className="ln-form">
          <label className="ln-label">
            <span>New password</span>
            <div className="ln-field">
              <input
                className="ln-input"
                type={showPwd ? 'text' : 'password'}
                placeholder="At least 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button type="button" className="ln-eye" onClick={() => setShowPwd(p => !p)} tabIndex={-1} aria-label={showPwd ? 'Hide password' : 'Show password'}>
                {showPwd ? <BsEyeSlashFill /> : <BsEyeFill />}
              </button>
            </div>
          </label>

          <label className="ln-label">
            <span>Confirm password</span>
            <div className="ln-field">
              <input
                className="ln-input"
                type={showConfirmPwd ? 'text' : 'password'}
                placeholder="Repeat new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button type="button" className="ln-eye" onClick={() => setShowConfirmPwd(p => !p)} tabIndex={-1} aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}>
                {showConfirmPwd ? <BsEyeSlashFill /> : <BsEyeFill />}
              </button>
            </div>
          </label>

          <button type="submit" className="ln-submit" disabled={isLoading}>
            {isLoading ? <><Loader size="sm" tone="light" style={{ marginRight: 8 }} /> Updating…</> : 'Update password'}
          </button>
        </form>
      )}

      {step === 'success' && (
        <div className="ln-form">
          <div className="ln-error ln-error--success">
            <BsCheckCircleFill />
            <span>Your password has been updated successfully.</span>
          </div>
          <button type="button" className="ln-submit" onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </button>
        </div>
      )}

      {step !== 'success' && (
        <p className="ln-footer-note">
          Remember your password? <Link to="/login" className="ln-forgot">Sign in</Link>
        </p>
      )}
    </AuthShell>
  );
};

export default ForgotPassword;
